/**
 * Serial request queue ("bus") — one in-flight task per key at a time,
 * responses resolve in submission order.
 */
type BusTask<T> = {
  run: () => Promise<T>
  resolve: (value: T) => void
  reject: (reason: unknown) => void
}

export function createRequestBus() {
  const queues = new Map<string, BusTask<unknown>[]>()
  const draining = new Map<string, boolean>()

  async function drain(key: string) {
    if (draining.get(key)) return
    draining.set(key, true)

    const queue = queues.get(key)
    while (queue && queue.length > 0) {
      const task = queue.shift()!
      try {
        const result = await task.run()
        task.resolve(result)
      } catch (error) {
        task.reject(error)
      }
    }

    draining.set(key, false)
  }

  function enqueue<T>(key: string, run: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!queues.has(key)) {
        queues.set(key, [])
      }
      queues.get(key)!.push({
        run,
        resolve: resolve as (value: unknown) => void,
        reject,
      })
      void drain(key)
    })
  }

  return { enqueue }
}
